import React, { useState, useEffect, useCallback } from 'react';
import { cityBody } from '@carboncare/schemas';
import { preflight } from '../../services/api/schemaPreflight';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  Tooltip,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Skeleton,
} from '@mui/material';
import {
  Add as AddIcon,
  LocationCity as CityIcon,
  Map as MapIcon,
  Public as PublicIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import apiClient from '../../services/api';
import { useCoordinatePaste } from '../../hooks/useCoordinatePaste';
import PageHeader from '../../components/common/PageHeader';
import {
  formatCoord2,
  StatusSnackbars,
  CountChip,
  usePaginationState,
  useReferenceSearch,
  SearchToolbar,
  NoResultsRow,
  CrudTablePagination,
  DeleteConfirmDialog,
  SkeletonActionCell,
  EditDeleteActions,
  CrudFormDialog,
  LatLonFields,
  validateDecimal,
} from './_shared';

// Cities are stored lowercase in the backend; display them title-cased.
// Capitalize the first *letter* of each word, skipping any leading symbols
// (e.g. "'abasan al kabirah" -> "'Abasan Al Kabirah").
const formatCityName = (name?: string): string =>
  (name ?? '')
    .split(' ')
    .map((word) => word.toLowerCase().replace(/\p{L}/u, (letter) => letter.toUpperCase()))
    .join(' ');

interface City {
  id?: number;
  city: string;
  state_province?: string;
  country: string;
  country_name?: string;
  lat: string;
  lon: string;
  region?: string;
  created_at?: string;
  updated_at?: string;
}

const CitiesPage: React.FC = () => {
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [countries, setCountries] = useState<string[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const { page, setPage, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePaginationState(20);
  const { search, matchMode, searchProps } = useReferenceSearch(setPage);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingCity, setEditingCity] = useState<City | null>(null);
  const [formData, setFormData] = useState<City>({
    city: '',
    state_province: '',
    country: '',
    country_name: '',
    lat: '',
    lon: '',
    region: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const handlePasteCoordinates = useCoordinatePaste(setFormData as any, setFormErrors);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cityToDelete, setCityToDelete] = useState<City | null>(null);

  const handleFormChange = (field: keyof City) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value: any = event.target.value;

    if (field === 'country') {
      value = value.toUpperCase();
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (formErrors[field]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const fetchCities = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await apiClient.get('/data-management/cities', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search,
          matchMode,
          country: countryFilter,
          sortBy: 'city',
          sortOrder: 'asc'
        }
      });

      if (response.data.success) {
        setCities(response.data.data);
        setTotalCount(response.data.pagination.total);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch cities');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, matchMode, countryFilter]);

  const fetchCountries = async () => {
    try {
      const response = await apiClient.get('/data-management/cities/countries');
      if (response.data.success) {
        setCountries(response.data.data);
      }
    } catch (err: any) {
      console.error('Failed to fetch countries:', err);
    }
  };

  useEffect(() => {
    fetchCities();
  }, [fetchCities]);

  useEffect(() => {
    fetchCountries();
  }, []);

  const handleCountryFilterChange = (event: any) => {
    setCountryFilter(event.target.value);
    setPage(0);
  };

  const handleOpenDialog = (city?: City) => {
    if (city) {
      setEditingCity(city);
      // only the editable schema fields; id/created_at/updated_at would be
      // rejected by the strict update schema on PUT
      setFormData({
        city: city.city,
        state_province: city.state_province || '',
        country: city.country,
        country_name: city.country_name || '',
        lat: city.lat,
        lon: city.lon,
        region: city.region || ''
      });
    } else {
      setEditingCity(null);
      setFormData({
        city: '',
        state_province: '',
        country: '',
        country_name: '',
        lat: '',
        lon: '',
        region: ''
      });
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCity(null);
    setFormErrors({});
    setFormData({
      city: '',
      state_province: '',
      country: '',
      country_name: '',
      lat: '',
      lon: '',
      region: ''
    });
  };

  const handleSave = async () => {
    const newErrors: Record<string, string> = {};
    if (!formData.city) newErrors.city = 'City is required';
    if (!formData.country) newErrors.country = 'Country Code is required';
    const latError = validateDecimal(formData.lat, 'Latitude');
    const lonError = validateDecimal(formData.lon, 'Longitude');
    if (latError) newErrors.lat = latError;
    if (lonError) newErrors.lon = lonError;

    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      return;
    }

    try {
      const dataToSend = {
        ...formData,
        city: formData.city.toLowerCase(),
        lat: Number.parseFloat(formData.lat),
        lon: Number.parseFloat(formData.lon)
      };

      if (editingCity) {
        const response = await apiClient.put(
          `/data-management/cities/${editingCity.id}`,
          dataToSend
        );
        if (response.data.success) {
          setSuccess('City updated successfully');
        }
      } else {
        preflight(cityBody, dataToSend);
        const response = await apiClient.post('/data-management/cities', dataToSend);
        if (response.data.success) {
          setSuccess('City created successfully');
        }
      }

      handleCloseDialog();
      fetchCities();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save city');
    }
  };

  const handleDeleteClick = (city: City) => {
    setCityToDelete(city);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!cityToDelete) return;

    try {
      const response = await apiClient.delete(
        `/data-management/cities/${cityToDelete.id}`
      );
      if (response.data.success) {
        setSuccess('City deleted successfully');
        fetchCities();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete city');
    } finally {
      setDeleteDialogOpen(false);
      setCityToDelete(null);
    }
  };

  return (
    <Box>
      <PageHeader
        description="View and maintain the city reference dataset."
        actions={
          <>
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchCities}
              variant="outlined"
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add City
            </Button>
          </>
        }
      />

      <StatusSnackbars
        error={error}
        success={success}
        onClearError={() => setError('')}
        onClearSuccess={() => setSuccess('')}
      />

      <Paper sx={{ p: 2, mb: 3 }}>
        <SearchToolbar
          placeholder="Search by name, city, state, or country..."
          {...searchProps}
          countSlot={<CountChip loading={loading} count={totalCount} label="cities" />}
          rightSlot={
            <FormControl sx={{ minWidth: 200, margin: 0 }}>
              <InputLabel>Filter by Country</InputLabel>
              <Select
                value={countryFilter}
                onChange={handleCountryFilterChange}
                label="Filter by Country"
              >
                <MenuItem value="">All Countries</MenuItem>
                {countries.map((country) => (
                  <MenuItem key={country} value={country}>
                    {country}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          }
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>City</TableCell>
              <TableCell>State/Province</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Coordinates</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array.from({ length: 10 }, (_, i) => `skeleton-${i}`).map((rowKey, idx) => (
                  <TableRow key={rowKey}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Skeleton variant="circular" width={18} height={18} />
                        <Skeleton variant="text" width={`${50 + ((idx * 7) % 30)}%`} />
                      </Box>
                    </TableCell>
                    <TableCell><Skeleton variant="text" width={110} /></TableCell>
                    <TableCell><Skeleton variant="rounded" width={100} height={22} sx={{ borderRadius: '12px' }} /></TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Skeleton variant="circular" width={16} height={16} />
                        <Skeleton variant="text" width={120} sx={{ fontSize: '0.75rem' }} />
                      </Box>
                    </TableCell>
                    <SkeletonActionCell />
                  </TableRow>
                ))
              : cities.length === 0
                ? <NoResultsRow colSpan={5} search={search} matchMode={matchMode} />
                : cities.map((city) => (
                  <TableRow key={city.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CityIcon fontSize="small" color="action" />
                        {formatCityName(city.city)}
                      </Box>
                    </TableCell>
                    <TableCell>{city.state_province || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={city.country}
                        size="small"
                        variant="outlined"
                        icon={<PublicIcon />}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={`Lat: ${city.lat}, Lon: ${city.lon}`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <MapIcon fontSize="small" color="action" />
                          <Typography variant="caption">
                            {formatCoord2(city.lat)}°, {formatCoord2(city.lon)}°
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <EditDeleteActions
                      editColor="primary"
                      onEdit={() => handleOpenDialog(city)}
                      onDelete={() => handleDeleteClick(city)}
                    />
                  </TableRow>
                ))}
          </TableBody>
        </Table>
        <CrudTablePagination
          loading={loading}
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <CrudFormDialog
        open={openDialog}
        title={editingCity ? 'Edit City' : 'Add New City'}
        onClose={handleCloseDialog}
        onSave={handleSave}
        saveLabel={editingCity ? 'Update' : 'Create'}
        saveButtonProps={{ color: 'primary' }}
      >
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={handleFormChange('city')}
                required
                error={!!formErrors.city}
                helperText={formErrors.city}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="State/Province"
                value={formData.state_province}
                onChange={handleFormChange('state_province')}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Country Code"
                value={formData.country}
                onChange={handleFormChange('country')}
                required
                inputProps={{ maxLength: 2 }}
                error={!!formErrors.country}
                helperText={formErrors.country}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Country Name"
                value={formData.country_name}
                onChange={handleFormChange('country_name')}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Region"
                value={formData.region}
                onChange={handleFormChange('region')}
              />
            </Grid>
            <LatLonFields
              latValue={formData.lat}
              lonValue={formData.lon}
              onLatChange={handleFormChange('lat')}
              onLonChange={handleFormChange('lon')}
              onPaste={handlePasteCoordinates}
              latError={formErrors.lat}
              lonError={formErrors.lon}
            />
          </Grid>
      </CrudFormDialog>

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
      >
        Are you sure you want to delete this city?
        <br />
        <strong>{cityToDelete?.city}</strong>, {cityToDelete?.country}
      </DeleteConfirmDialog>
    </Box>
  );
};

export default CitiesPage;
